/**
 * Cluster-selection reconciliation (STORY-256 / BUG-1.4.0_2 root fix).
 *
 * The chat request carries `cluster_ids` straight from the persisted store. When
 * a cluster is removed/re-added (or re-seeded → new SQLite PK, or changed in
 * another tab), the stored selection goes STALE: the backend then drops the
 * unknown id and the fan-out collapses to the default cluster. This module keeps
 * the selection in sync with the live cluster list, both in the selector (on
 * mount/focus) and as a final guard right before send.
 */

export type ClusterMode = 'single' | 'sequential' | 'parallel';

export interface ClusterReconcileResult {
  /** selection intersected with the live cluster ids */
  selectedClusterIds: string[];
  /** mode, downgraded to 'single' when a fan-out no longer has ≥2 valid clusters */
  clusterMode: ClusterMode;
  /** ids that were in the selection but are no longer live */
  dropped: string[];
  /** true when the selection or mode had to change */
  changed: boolean;
}

/**
 * Reconcile a stored cluster selection against the currently-live cluster ids.
 * Pure — no I/O — so it can back both the selector and the send-path guard.
 */
export function reconcileClusterSelection(
  selected: string[] | undefined | null,
  liveClusterIds: string[],
  mode: ClusterMode | undefined | null,
): ClusterReconcileResult {
  const live = new Set(liveClusterIds.map(String));
  const sel = (selected || []).map(String);
  const kept = sel.filter((id) => live.has(id));
  const dropped = sel.filter((id) => !live.has(id));

  let clusterMode: ClusterMode = mode || 'single';
  // Fan-out needs ≥2 valid clusters; a collapsed selection downgrades to single
  // so the request routes to the one surviving cluster instead of fanning out
  // over a phantom (which the backend would default to the base cluster).
  if ((clusterMode === 'sequential' || clusterMode === 'parallel') && kept.length < 2) {
    clusterMode = 'single';
  }

  const changed = dropped.length > 0 || clusterMode !== (mode || 'single');
  return { selectedClusterIds: kept, clusterMode, dropped, changed };
}

// ---------------------------------------------------------------------------
// In-memory cache of the last-fetched live cluster ids.
//
// The ClusterModeSelector fetches the live list on mount/focus; useChatService
// reads it as a send-time guard for the window between a cluster change and the
// next focus-refresh. Deliberately NOT persisted (client-derived, transient) —
// so it never becomes stale itself across reloads.
// ---------------------------------------------------------------------------
let _knownClusterIds: string[] | null = null;

export function setKnownClusterIds(ids: string[]): void {
  _knownClusterIds = ids.map(String);
}

export function getKnownClusterIds(): string[] | null {
  return _knownClusterIds;
}

// ---------------------------------------------------------------------------
// STORY-300: the live clusters by name, for @-mention suggestions.
//
// Same lifecycle as the id cache above (filled by ClusterModeSelector on
// mount/focus, never persisted), plus a subscription so the chat input can
// re-render when the list arrives. The backend is the single source of truth
// for what a mention means — the client sends the raw text and never strips.
// ---------------------------------------------------------------------------

export interface KnownCluster {
  id: string;
  name: string;
  /** what the user types after '@' (mentionSlug(name)) */
  slug: string;
}

export interface KnownClustersSnapshot {
  clusters: KnownCluster[];
  /** >1 active cluster and not cluster-bound: only then are suggestions offered */
  multiClusterAvailable: boolean;
}

const EMPTY_SNAPSHOT: KnownClustersSnapshot = { clusters: [], multiClusterAvailable: false };
let _knownClusters: KnownClustersSnapshot = EMPTY_SNAPSHOT;
const _listeners = new Set<() => void>();

const LETTER_DIGIT_DOT = /[\p{L}\p{N}.]/u;
const COMBINING_MARK = /\p{M}/u;

/**
 * The form a cluster is typed as after '@'. MUST equal
 * chat-client/backend/cluster_mentions.py::mention_slug —
 * tests/fixtures/cluster_mention_slugs.json pins the parity on both sides.
 * NFKC, casefold (toLowerCase plus the two folds it lacks: ß→ss, final ς→σ),
 * combining marks dropped, every run of characters that are neither letters,
 * digits nor '.' becomes one '-', and '-'/'.' are trimmed from both ends.
 */
export function mentionSlug(name: string | null | undefined): string {
  const folded = String(name ?? '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ς/g, 'σ')
    .normalize('NFKC');
  let out = '';
  let pendingSep = false;
  for (const ch of folded) {
    if (COMBINING_MARK.test(ch)) continue;
    if (LETTER_DIGIT_DOT.test(ch)) {
      if (pendingSep && out) out += '-';
      pendingSep = false;
      out += ch;
    } else {
      pendingSep = true;
    }
  }
  return out.replace(/^[-.]+|[-.]+$/g, '');
}

export function setKnownClusters(
  clusters: Array<{ id: string | number; name: string }>,
  multiClusterAvailable: boolean,
): void {
  _knownClusters = {
    clusters: clusters.map((c) => ({ id: String(c.id), name: c.name, slug: mentionSlug(c.name) })),
    multiClusterAvailable,
  };
  _listeners.forEach((fn) => fn());
}

export function getKnownClusters(): KnownClustersSnapshot {
  return _knownClusters;
}

export function subscribeKnownClusters(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/** Test seam: forget the cached list. */
export function resetKnownClusters(): void {
  _knownClusters = EMPTY_SNAPSHOT;
  _listeners.forEach((fn) => fn());
}

export interface MentionQuery {
  /** index of the '@' */
  start: number;
  /** end of the token under the caret (exclusive) */
  end: number;
  /** what has been typed after '@' up to the caret */
  partial: string;
}

const MENTION_BODY = /[\p{L}\p{N}_.-]/u;
const SPACE = /\s/u;

const LIST_SEP = /[,;:]/u;

/**
 * Is the '@' at `at` at a mention position? The start, after whitespace,
 * after a '(' at a word boundary, or after a , ; : glued to a previous
 * mention-shaped token («@alpha,@beta» yes, «web,@beta» no) — the backend's
 * rule (cluster_mentions._at_mention_position). Iterative, so a long
 * «@a,@a,@a…» chain stays linear.
 */
function atMentionPosition(text: string, at: number): boolean {
  const boundary = (k: number) => k < 0 || SPACE.test(text[k]);
  let p = at;
  for (;;) {
    if (boundary(p - 1) || (text[p - 1] === '(' && boundary(p - 2))) return true;
    if (p < 2 || !LIST_SEP.test(text[p - 1])) return false;
    let k = p - 2;
    while (k >= 0 && MENTION_BODY.test(text[k])) k -= 1;
    if (k === p - 2 || k < 0 || text[k] !== '@') return false;
    p = k;
  }
}

/**
 * The @-token the caret is in when it sits at a mention position (see
 * atMentionPosition) — else null. «user@» is not a mention, so it gets no list.
 */
export function mentionAtCaret(text: string, caret: number): MentionQuery | null {
  let i = caret;
  while (i > 0 && MENTION_BODY.test(text[i - 1])) i -= 1;
  const at = i - 1;
  if (at < 0 || text[at] !== '@') return null;
  if (!atMentionPosition(text, at)) return null;
  let end = caret;
  while (end < text.length && MENTION_BODY.test(text[end])) end += 1;
  return { start: at, end, partial: text.slice(at + 1, caret) };
}

/** Live clusters whose @slug starts with what was typed (compared as slugs). */
export function filterMentionClusters(clusters: KnownCluster[], partial: string): KnownCluster[] {
  const p = mentionSlug(partial);
  return clusters.filter((c) => c.slug !== '' && c.slug.startsWith(p));
}
